<template>
  <div class="treasury-dashboard">
    <div class="page-header">
      <div>
        <h1 class="title is-4">ຍອດທຽບທະນາຄານ (Treasury)</h1>
        <p class="subtitle is-6 has-text-grey">
          ຍອດທີ່ຄຳນວນຈາກບັນທຶກພາຍໃນ — ບໍ່ແມ່ນຍອດທະນາຄານສົດ
        </p>
      </div>
      <div class="header-actions">
        <span v-if="treasury" class="computed-at">ຄຳນວນເມື່ອ: {{ treasury.computed_at }}</span>
        <vs-button flat icon="refresh" :loading="loading" @click="fetchTreasury">ໂຫລດຄືນ</vs-button>
      </div>
    </div>

    <!-- Ledger drift / degraded warning -->
    <div v-if="treasury && treasury.ledger_drift && treasury.ledger_drift.degraded" class="notification is-warning drift-banner">
      <strong>⚠️ ຂໍ້ມູນບັນຊີບໍ່ກົງກັນ (degraded)</strong> —
      ຍອດທີ່ສະແດງອາດບໍ່ຖືກຕ້ອງ. ກວດສອບການ reconcile ກ່ອນຕັດສິນໃຈ.
      <ul class="drift-detail">
        <li v-if="treasury.ledger_drift.permanently_failed_count > 0">
          ການ sync ບັນຊີລົ້ມເຫລວຖາວອນ: {{ treasury.ledger_drift.permanently_failed_count }}
        </li>
        <li v-if="treasury.ledger_drift.unbalanced_wallet_count > 0">
          ກະເປົ໋າທີ່ບໍ່ສົມດຸນ: {{ treasury.ledger_drift.unbalanced_wallet_count }}
        </li>
        <li v-if="!isZero(treasury.ledger_drift.difference)">
          ຄວາມຕ່າງ wallet vs ledger: {{ formatCurrency(treasury.ledger_drift.difference) }}
        </li>
      </ul>
    </div>

    <div v-if="loadError" class="notification is-danger">
      ບໍ່ສາມາດໂຫລດຂໍ້ມູນ Treasury ໄດ້.
    </div>

    <div v-if="treasury" class="cards">
      <!-- Float -->
      <div class="card metric-card">
        <header class="card-header"><p class="card-header-title">ຍອດເງິນສົດທີ່ຄາດ (Bank float)</p></header>
        <div class="card-content">
          <p class="metric-value">{{ formatCurrency(treasury.float.total) }}</p>
          <table class="table is-fullwidth is-narrow by-source">
            <tbody>
              <tr v-for="src in treasury.float.by_source" :key="src.account">
                <td>{{ src.label }}</td>
                <td class="has-text-right">{{ formatCurrency(src.balance) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Liability -->
      <div class="card metric-card">
        <header class="card-header"><p class="card-header-title">ໜີ້ສິນຕໍ່ສາຂາ (Liability)</p></header>
        <div class="card-content">
          <div class="kv"><span>ສາຂາເຕີມເງິນ (owed)</span><span>{{ formatCurrency(treasury.liability.amount_owed) }}</span></div>
          <div class="kv"><span>ສາຂາຕິດໜີ້ (receivables)</span><span>{{ formatCurrency(treasury.liability.receivables) }}</span></div>
          <div class="kv kv-strong"><span>ສຸດທິ (net)</span><span>{{ formatCurrency(treasury.liability.net) }}</span></div>
        </div>
      </div>

      <!-- Gap -->
      <div class="card metric-card">
        <header class="card-header"><p class="card-header-title">ສ່ວນຕ່າງ (Gap)</p></header>
        <div class="card-content">
          <p class="metric-value">{{ formatCurrency(treasury.gap) }}</p>
          <p class="has-text-grey is-size-7">float − liability (≈ ລາຍຮັບທີ່ HAL ເກັບໄວ້)</p>
          <div class="kv"><span>ກວດສອບ revenue</span><span>{{ formatCurrency(treasury.revenue_check) }}</span></div>
        </div>
      </div>
    </div>

    <p v-if="treasury && treasury.ledger_drift && treasury.ledger_drift.last_reconciled_at" class="last-recon has-text-grey is-size-7">
      reconcile ຫລ້າສຸດ: {{ treasury.ledger_drift.last_reconciled_at }}
    </p>
  </div>
</template>

<script>
import walletService from '@/services/walletService';
import { formatCurrency, formatErrorMessage } from '@/services/walletFormatters';

export default {
  name: 'TreasuryDashboard',
  data() {
    return {
      treasury: null,
      loading: false,
      loadError: false,
    };
  },
  mounted() {
    this.fetchTreasury();
  },
  methods: {
    async fetchTreasury() {
      this.loading = true;
      this.loadError = false;
      try {
        // Calls the Go wallet service DIRECTLY with a JWT (walletService.getWalletAxios),
        // matching the established admin pattern (manual top-up, pubnub-token). The Go
        // treasury endpoint audits each read server-side (VIEW_TREASURY), so it is not
        // routed through Laravel's HMAC/circuit-breaker proxy.
        const result = await walletService.getTreasury(this.$axios);
        this.treasury = result.data || result;
      } catch (error) {
        console.error('Fetch treasury error:', error.response?.data || error);
        this.loadError = true;
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, formatErrorMessage(error, 'ບໍ່ສາມາດໂຫລດ Treasury ໄດ້'));
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(amount) {
      return formatCurrency(amount);
    },
    isZero(amount) {
      return Math.abs(Number(amount) || 0) < 0.005;
    },
  },
};
</script>

<style scoped>
.treasury-dashboard { padding: 1rem; }
.page-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1rem; }
.header-actions { display: flex; align-items: center; gap: 0.75rem; }
.computed-at { font-size: 0.8rem; color: #888; }
.drift-banner { margin-bottom: 1rem; }
.drift-detail { margin-top: 0.5rem; padding-left: 1.25rem; list-style: disc; }
.cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 1rem; }
.metric-card .metric-value { font-size: 1.6rem; font-weight: 700; margin-bottom: 0.5rem; }
.by-source td { padding: 0.2rem 0.4rem; font-size: 0.85rem; }
.kv { display: flex; justify-content: space-between; padding: 0.25rem 0; }
.kv-strong { font-weight: 700; border-top: 1px solid #eee; margin-top: 0.25rem; padding-top: 0.5rem; }
.last-recon { margin-top: 1rem; }
</style>
